import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Activity, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { IntakeField } from "@/components/IntakeField";
import {
  applyAnswerPatch, evaluateShowIf, getSafetyAlerts, ALERT_RULE_MESSAGES,
} from "@/lib/formEngine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicIntakeForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/intake/${token}`);
        if (dead) return;
        setData(r.data);
        if (r.data.status === "submitted") {
          setSubmittedAt(r.data.submitted_at);
        }
        // Initialise answers from prefill
        setAnswers({ ...(r.data.prefill || {}) });
      } catch (e) {
        if (dead) return;
        setData({ status: "error", message: e?.response?.data?.detail || e.message });
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [token]);

  const groups = useMemo(() => {
    if (!data?.template?.fields) return [];
    // v2.0 form engine — filter each field through evaluateShowIf
    // so only visible fields participate in the section list. The
    // filter is reactive: re-evaluating on every `answers` change
    // is what makes conditional visibility work at runtime.
    const allFields = data.template.fields;
    const visible = allFields.filter((f) => evaluateShowIf(f, answers, allFields));
    const order = [];
    const byGroup = new Map();
    for (const f of visible) {
      const g = f.group || "Details";
      if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g); }
      byGroup.get(g).push(f);
    }
    return order.map((name) => ({ name, fields: byGroup.get(name) }));
  }, [data, answers]);

  // Safety-critical alerts — computed from the FULL field list (not
  // just the visible slice) so an answer inside a now-hidden branch
  // stops triggering when the branch collapses. v2.0 spec §4.3.
  const safetyAlerts = useMemo(() => {
    if (!data?.template?.fields) return [];
    return getSafetyAlerts(data.template.fields, answers);
  }, [data, answers]);

  function setVal(id, v) {
    // v2.0 exclusiveGroup + exclusiveWith enforcement — flip through
    // the pure engine which also clears any peer / exclusive-with
    // fields so the operator's onboarding rules always hold.
    setAnswers((a) => applyAnswerPatch(data?.template?.fields || [], a, id, v));
  }

  async function submit(e) {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/intake/${token}`, { answers });
      setSubmittedAt(r.data.submitted_at);
      toast.success("Thanks — your form was submitted.");
    } catch (e) {
      toast.error("Could not submit", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ----- Render states -----

  if (loading) {
    return <Shell><div className="text-sm text-zinc-500">Loading…</div></Shell>;
  }

  if (data?.status === "expired" || data?.status === "error") {
    return (
      <Shell>
        <div className="flex items-start gap-3" data-testid="intake-expired">
          <div className="h-10 w-10 grid place-items-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-zinc-950">
              This link is no longer active
            </h1>
            <p className="mt-1 text-sm text-zinc-600">
              {data?.status === "expired"
                ? "It has been more than 2 months since the form was sent. Please contact us for a new link."
                : data?.message || "We couldn't open this form. Please contact us."}
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (submittedAt || data?.status === "submitted") {
    return (
      <Shell>
        <div className="flex items-start gap-3" data-testid="intake-submitted">
          <div className="h-10 w-10 grid place-items-center rounded-full bg-emerald-100 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-zinc-950">Thanks — we've got your form</h1>
            <p className="mt-1 text-sm text-zinc-600">
              You can close this page. See you at your appointment!
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  const apptDate = data?.appointment_datetime_iso
    ? new Date(data.appointment_datetime_iso)
    : null;
  const apptStr = apptDate
    ? apptDate.toLocaleString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-8" data-testid="intake-form">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Welcome
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            {data?.template?.name || "Registration form"}
          </h1>
          {data?.template?.description && (
            <p className="mt-2 text-sm text-zinc-600">{data.template.description}</p>
          )}
          {apptStr && (
            <p className="mt-3 text-sm text-zinc-700 bg-zinc-50 px-3 py-2 rounded border border-zinc-200">
              Your appointment: <span className="font-medium">{data.appointment_type_name}</span>
              {" "}on <span className="font-medium">{apptStr}</span>
              {data.calendar_name && <> with <span className="font-medium">{data.calendar_name}</span></>}
            </p>
          )}
        </div>

        {groups.map((g) => (
          <section key={g.name} className="space-y-4">
            <h2 className="font-display text-lg font-semibold text-zinc-950 border-b border-zinc-200 pb-1.5">
              {g.name}
            </h2>
            <div className="space-y-4">
              {g.fields.map((f) => (
                <IntakeField
                  key={f.id}
                  field={f}
                  value={answers[f.id]}
                  onChange={(v) => setVal(f.id, v)}
                  uploadEndpoint={
                    f.type === "file"
                      ? `${API}/public/intake/${token}/upload/${f.id}`
                      : undefined
                  }
                  ocrStatusEndpoint={
                    f.type === "file" && f.ocr_target_field_id
                      ? `${API}/public/intake/${token}/upload/${f.id}/ocr`
                      : undefined
                  }
                  onAcceptOcrSuggestion={(suggestion) => {
                    if (f.ocr_target_field_id) setVal(f.ocr_target_field_id, suggestion);
                  }}
                />
              ))}
            </div>
          </section>
        ))}

        {safetyAlerts.length > 0 && (
          <div
            className="rounded-md border border-rose-300 bg-rose-50 p-4"
            role="alert"
            data-testid="safety-critical-banner"
          >
            <div className="flex items-start gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-rose-900 text-sm">
                  We noticed {safetyAlerts.length}{" "}
                  {safetyAlerts.length === 1 ? "answer" : "answers"}{" "}
                  we'd like our team to look at
                </div>
                <p className="mt-1 text-xs text-rose-900">
                  You can still submit — a clinician will review these
                  before your appointment. Feel free to add extra
                  detail in the notes field.
                </p>
                <ul className="mt-2 space-y-1 text-xs text-rose-950">
                  {safetyAlerts.map((a) => (
                    <li key={a.fieldId} className="flex items-start gap-1.5"
                        data-testid={`safety-critical-item-${a.fieldId}`}>
                      <span className="font-medium">{a.label}:</span>
                      <span className="opacity-90">
                        {ALERT_RULE_MESSAGES[a.rule] || "Requires clinician review."}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-zinc-200">
          <Button
            type="submit"
            disabled={submitting}
            data-testid="intake-submit-button"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            size="lg"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Submitting…" : "Submit registration"}
          </Button>
          <p className="mt-2 text-xs text-zinc-500">
            * Required fields. Your information is sent securely to our clinical team.
          </p>
        </div>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 h-16 flex items-center gap-2.5">
          <div className="h-8 w-8 grid place-items-center bg-zinc-950 text-white rounded-md">
            <Activity className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <div className="font-display text-lg font-bold tracking-tight text-zinc-950">
            Living Energy <span className="text-zinc-400">·</span> Brain Body Rehab
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <Card className="p-6 sm:p-10 border border-zinc-200 shadow-sm">
          {children}
        </Card>
      </main>
    </div>
  );
}
